import { BASE_URL, fetchWithAuth } from "./client";

const SOCIAL_MEDIA_BASE_URL = (
  process.env.REACT_APP_SOCIAL_MEDIA_API ||
  process.env.REACT_APP_API_BASE ||
  BASE_URL ||
  ""
).replace(/\/+$/, "");

const getErrorMessage = async (response, fallback) => {
  const error = await response.json().catch(() => ({}));
  const detail = error.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (detail?.error) return detail.error;
  return error.error || error.message || fallback;
};

export const getOAuthUrl = async (platform, brandId = null) => {
  const params = new URLSearchParams();
  if (brandId) {
    params.set("brand_id", brandId);
  }

  const query = params.toString();
  const response = await fetchWithAuth(`${SOCIAL_MEDIA_BASE_URL}/api/social-media/platforms/${platform}/oauth-url${query ? `?${query}` : ""}`);

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, `Failed to get OAuth URL for ${platform}`));
  }

  const data = await response.json();
  return data.data || data;
};

export const getFacebookOAuthUrl = async (brandId = null) => getOAuthUrl("facebook", brandId);

export const completeOAuth = async ({ platform, code, state }) => {
  const response = await fetch(`${SOCIAL_MEDIA_BASE_URL}/api/social-media/platforms/${platform}/callback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, state }),
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, `Failed to complete OAuth for ${platform}`));
  }

  const data = await response.json();
  return data.data || data;
};

export const selectFacebookPage = async ({ oauthState, pageId, pageName, pageAccessToken }) => {
  const response = await fetch(`${SOCIAL_MEDIA_BASE_URL}/api/social-media/platforms/facebook/select-page`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      oauthState: oauthState,
      pageId: pageId,
    }),
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to connect Facebook page"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Subscribe a Facebook Page to the Claw9 webhook to receive messages
 * This should be called after selectFacebookPage succeeds
 */
export const subscribeFacebookPage = async ({ accountId, pageId }) => {
  const params = new URLSearchParams({ account_id: accountId, page_id: pageId });
  const response = await fetchWithAuth(
    `${SOCIAL_MEDIA_BASE_URL}/api/claw9/facebook/webhook/subscribe-page?${params}`,
    { method: "POST" }
  );

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to subscribe page to webhook"));
  }

  const data = await response.json();
  return data.data || data;
};

// ==================== Facebook Posts & Comments API ====================

/**
 * List posts from connected Facebook Page
 */
export const fetchFacebookPosts = async (accountId, { limit = 25, after } = {}) => {
  const params = new URLSearchParams({ account_id: accountId, limit: String(limit) });
  if (after) params.set("after", after);

  const response = await fetchWithAuth(`${SOCIAL_MEDIA_BASE_URL}/api/claw9/facebook/posts?${params}`);
  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to fetch Facebook posts"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Create a new post on Facebook Page
 */
export const createFacebookPost = async (accountId, { message, link }) => {
  const params = new URLSearchParams({ account_id: accountId });
  const response = await fetchWithAuth(`${SOCIAL_MEDIA_BASE_URL}/api/claw9/facebook/posts?${params}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, link }),
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to create Facebook post"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Get comments on a Facebook post
 */
export const fetchFacebookComments = async (accountId, postId, { limit = 50, after } = {}) => {
  const params = new URLSearchParams({ account_id: accountId, limit: String(limit) });
  if (after) params.set("after", after);

  const response = await fetchWithAuth(
    `${SOCIAL_MEDIA_BASE_URL}/api/claw9/facebook/posts/${encodeURIComponent(postId)}/comments?${params}`
  );
  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to fetch comments"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Comment on a post as the Page
 */
export const createFacebookComment = async (accountId, postId, message) => {
  const params = new URLSearchParams({ account_id: accountId });
  const response = await fetchWithAuth(
    `${SOCIAL_MEDIA_BASE_URL}/api/claw9/facebook/posts/${encodeURIComponent(postId)}/comments?${params}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message }),
    }
  );

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to post comment"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Reply to a comment
 */
export const replyToFacebookComment = async (accountId, commentId, message) => {
  const params = new URLSearchParams({ account_id: accountId });
  const response = await fetchWithAuth(
    `${SOCIAL_MEDIA_BASE_URL}/api/claw9/facebook/comments/${encodeURIComponent(commentId)}/reply?${params}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message }),
    }
  );

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to reply to comment"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Get replies to a comment
 */
export const fetchFacebookCommentReplies = async (accountId, commentId, { limit = 50, after } = {}) => {
  const params = new URLSearchParams({ account_id: accountId, limit: String(limit) });
  if (after) params.set("after", after);

  const response = await fetchWithAuth(
    `${SOCIAL_MEDIA_BASE_URL}/api/claw9/facebook/comments/${encodeURIComponent(commentId)}/replies?${params}`
  );
  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to fetch replies"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Hide or unhide a comment
 */
export const hideFacebookComment = async (accountId, commentId, isHidden = true) => {
  const params = new URLSearchParams({ account_id: accountId });
  const response = await fetchWithAuth(
    `${SOCIAL_MEDIA_BASE_URL}/api/claw9/facebook/comments/${encodeURIComponent(commentId)}/hide?${params}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_hidden: isHidden }),
    }
  );

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, isHidden ? "Failed to hide comment" : "Failed to unhide comment"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Delete a comment (only works for Page's own comments)
 */
export const deleteFacebookComment = async (accountId, commentId) => {
  const params = new URLSearchParams({ account_id: accountId });
  const response = await fetchWithAuth(
    `${SOCIAL_MEDIA_BASE_URL}/api/claw9/facebook/comments/${encodeURIComponent(commentId)}?${params}`,
    { method: "DELETE" }
  );

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to delete comment"));
  }

  const data = await response.json();
  return data.data || data;
};

// ==================== Scheduled Posts API ====================

/**
 * Create a scheduled post
 */
export const createScheduledPost = async ({
  integrationId,
  content,
  scheduledAt,
  status = "scheduled",
  mediaUrls = [],
  platformSettings = {}
}) => {
  const response = await fetchWithAuth(`${SOCIAL_MEDIA_BASE_URL}/api/social-media/posts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      integration_id: integrationId,
      content,
      scheduled_at: scheduledAt,
      status,
      media_urls: mediaUrls,
      platform_settings: platformSettings,
    }),
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to create post"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * List scheduled posts
 */
export const fetchScheduledPosts = async ({ status, integrationId, limit = 50, lastKey } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (integrationId) params.set("integration_id", integrationId);
  if (limit) params.set("limit", String(limit));
  if (lastKey) params.set("last_key", lastKey);

  const response = await fetchWithAuth(`${SOCIAL_MEDIA_BASE_URL}/api/social-media/posts?${params}`);
  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to fetch posts"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Get a single post
 */
export const fetchScheduledPost = async (postId) => {
  const response = await fetchWithAuth(`${SOCIAL_MEDIA_BASE_URL}/api/social-media/posts/${encodeURIComponent(postId)}`);
  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to fetch post"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Update a scheduled post
 */
export const updateScheduledPost = async (postId, { content, scheduledAt, mediaUrls }) => {
  const response = await fetchWithAuth(`${SOCIAL_MEDIA_BASE_URL}/api/social-media/posts/${encodeURIComponent(postId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      content,
      scheduled_at: scheduledAt,
      media_urls: mediaUrls,
    }),
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to update post"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Delete a scheduled post
 */
export const deleteScheduledPost = async (postId) => {
  const response = await fetchWithAuth(`${SOCIAL_MEDIA_BASE_URL}/api/social-media/posts/${encodeURIComponent(postId)}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to delete post"));
  }

  const data = await response.json();
  return data.data || data;
};

/**
 * Cross-post to multiple platforms
 */
export const createCrossPost = async ({
  integrationIds,
  content,
  scheduledAt,
  mediaUrls = [],
  platformSettings = {}
}) => {
  const response = await fetchWithAuth(`${SOCIAL_MEDIA_BASE_URL}/api/social-media/posts/cross-post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      integration_ids: integrationIds,
      content,
      scheduled_at: scheduledAt,
      media_urls: mediaUrls,
      platform_settings: platformSettings,
    }),
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, "Failed to create posts"));
  }

  const data = await response.json();
  return data.data || data;
};
